/**
 * Scaffolder field extension for the "Adding Users" step.
 *
 * Renders three stacked AddUserList panels — one for each persona:
 *   • Data Engineers
 *   • Data Analysts
 *   • Data Scientists
 *
 * The field value is an object: { data_engineers: UserEntry[], data_analysts: UserEntry[], data_scientists: UserEntry[] }
 *
 * Every user must be an admin account: only admin accounts get Databricks
 * access.
 */
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import { makeStyles } from '@material-ui/core/styles';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { AccountKind, accountKindRefusal } from '@internal/datasphere-personas';
import { AddUserList, UserEntry } from '../AddUserList';

export interface UsersFieldValue {
  data_engineers: UserEntry[];
  data_analysts: UserEntry[];
  data_scientists: UserEntry[];
  /** Comma-separated display names — computed automatically for the review screen. */
  data_engineers_names: string;
  data_analysts_names: string;
  data_scientists_names: string;
}

const EMPTY_VALUE: UsersFieldValue = {
  data_engineers: [],
  data_analysts: [],
  data_scientists: [],
  data_engineers_names: '',
  data_analysts_names: '',
  data_scientists_names: '',
};

const PERSONA_ROLES = {
  data_engineers: 'Data Engineer',
  data_analysts: 'Data Analyst',
  data_scientists: 'Data Scientist',
} as const;

/** Error messages for a field value; empty when the value is valid. */
export const usersErrors = (value: UsersFieldValue | undefined): string[] =>
  (Object.keys(PERSONA_ROLES) as (keyof typeof PERSONA_ROLES)[]).flatMap(
    persona =>
      (value?.[persona] ?? []).flatMap(
        user =>
          accountKindRefusal(user, AccountKind.Admin, PERSONA_ROLES[persona]) ??
          [],
      ),
  );

/** Field validation: blocks the step while any user has the wrong account kind. */
export const validateUsers = (
  value: UsersFieldValue | undefined,
  validation: { addError: (message: string) => void },
): void => {
  for (const error of usersErrors(value)) {
    validation.addError(error);
  }
};

const useStyles = makeStyles(theme => ({
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  divider: {
    marginBottom: theme.spacing(2),
  },
}));

export const AddUsersField = ({
  onChange,
  rawErrors,
  required,
  formData,
}: FieldExtensionComponentProps<UsersFieldValue>) => {
  const classes = useStyles();
  const value: UsersFieldValue = formData ?? EMPTY_VALUE;

  const handleChange =
    (
      persona: keyof Pick<
        UsersFieldValue,
        'data_engineers' | 'data_analysts' | 'data_scientists'
      >,
    ) =>
    (users: UserEntry[]) => {
      const names = users.map(u => u.displayName).join(', ');
      onChange({
        ...value,
        [persona]: users,
        [`${persona}_names`]: names,
      });
    };

  // Distribute raw errors per section: each error names its persona, for
  // example "data engineers: ..." or "Data Engineer must be an admin account".
  const errorsFor = (persona: string): string[] | undefined => {
    if (!rawErrors?.length) return undefined;
    const role = persona.replace('_', ' ').replace(/s$/, '');
    const matching = rawErrors.filter(e => e.toLowerCase().includes(role));
    return matching.length ? matching : undefined;
  };

  return (
    <Box>
      <Box className={classes.section}>
        <Typography variant="subtitle1" className={classes.sectionTitle}>
          Data Engineers
        </Typography>
        <AddUserList
          users={value.data_engineers}
          onChange={handleChange('data_engineers')}
          label="Search for a data engineer"
          helperText="Add users who will have the Data Engineer persona. Use their admin account."
          required={required}
          accountKind={AccountKind.Admin}
          rawErrors={errorsFor('data_engineers')}
        />
      </Box>

      <Divider className={classes.divider} />

      <Box className={classes.section}>
        <Typography variant="subtitle1" className={classes.sectionTitle}>
          Data Analysts
        </Typography>
        <AddUserList
          users={value.data_analysts}
          onChange={handleChange('data_analysts')}
          label="Search for a data analyst"
          helperText="Add users who will have the Data Analyst persona. Use their admin account."
          required={required}
          accountKind={AccountKind.Admin}
          rawErrors={errorsFor('data_analysts')}
        />
      </Box>

      <Divider className={classes.divider} />

      <Box className={classes.section}>
        <Typography variant="subtitle1" className={classes.sectionTitle}>
          Data Scientists
        </Typography>
        <AddUserList
          users={value.data_scientists}
          onChange={handleChange('data_scientists')}
          label="Search for a data scientist"
          helperText="Add users who will have the Data Scientist persona. Use their admin account."
          required={required}
          accountKind={AccountKind.Admin}
          rawErrors={errorsFor('data_scientists')}
        />
      </Box>
    </Box>
  );
};
